import { useMemo } from 'react';
import { Paper, Box, Chip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ReactECharts from 'echarts-for-react';
import {
  formatRupeesIndianCompact,
  premiumTooltip,
  standardGrid,
  indianValueAxis,
  indianCategoryAxis,
  defaultAnimation,
} from './echartsConfig';
import { labelToMonthKey } from './reportChartMonthUtils';
import { reportBobChartCanvasSx, reportChartPaperSx } from './reportChartLayout';
import ReportKpiStrip, { kpiMapToItems } from './ReportKpiStrip';
import ReportCardHeader, { ReportInsightBanner } from './ReportCardHeader';

const norm = s => String(s || '').toLowerCase();
const toNum = v => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const toNumArray = arr => (arr || []).map(toNum);
const sumArr = arr => (arr || []).reduce((s, v) => s + toNum(v), 0);

const CONFIRMED_GREEN = '#66BB6A';
const TENTATIVE_AMBER = '#FFC107';
const ACTUAL_PURPLE = '#9575CD';
const BUDGET_LINE = '#78909C';

const miniChipSx = (theme, toneMain) => ({
  fontWeight: 500,
  height: 22,
  border: '1px solid',
  backgroundColor: alpha(toneMain, 0.08),
  borderColor: alpha(toneMain, 0.22),
  color: theme.palette.text.primary,
  '& .MuiChip-label': { px: 0.75, py: 0, fontSize: '0.65rem' },
});

function findDs(datasets, needle) {
  return datasets.find(d => norm(d.label).includes(needle));
}

function alignActualToLabels(bobLabels, actualData, reportDateRange) {
  const actLabels = actualData?.labels || [];
  const actDatasets = actualData?.datasets || [];
  const budgetDs = findDs(actDatasets, 'budget') || actDatasets[0];
  const actualDs = findDs(actDatasets, 'actual') || actDatasets[1];
  if (!actualDs?.data) return bobLabels.map(() => 0);

  const actMap = new Map();
  actLabels.forEach((label, i) => {
    const key = labelToMonthKey(label);
    if (key) {
      actMap.set(key, toNum(actualDs.data[i]));
    }
  });

  return bobLabels.map(label => {
    const key = labelToMonthKey(label);
    return key ? (actMap.get(key) || 0) : 0;
  });
}

function tooltipFormatter(params) {
  if (!Array.isArray(params) || params.length === 0) return '';
  const title = `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:6px">${params[0].axisValueLabel}</div>`;

  const row = (label, value, color) => {
    const dot = color ? `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:6px"></span>` : '';
    return `<div style="display:flex;justify-content:space-between;align-items:center;gap:12px">${dot}<span style="flex:1">${label}</span><span style="font-weight:600;font-variant-numeric:tabular-nums">${formatRupeesIndianCompact(value)}</span></div>`;
  };

  let budget = 0, confirmed = 0, tentative = 0, pipeline = 0, actual = 0;
  let hasPipeline = false;
  params.forEach(p => {
    const L = norm(p.seriesName);
    if (L.includes('budget') || L.includes('pace') || L.includes('target')) budget = toNum(p.value);
    else if (L.includes('pipeline')) { pipeline = toNum(p.value); hasPipeline = true; }
    else if (L.includes('confirm') || L.includes('secured')) confirmed = toNum(p.value);
    else if (L.includes('tentative')) tentative = toNum(p.value);
    else if (L.includes('actual')) actual = toNum(p.value);
  });

  let html = title;
  html += row('Budget', budget, BUDGET_LINE);
  html += `<div style="border-top:1px solid rgba(148,163,184,0.15);margin:4px 0"></div>`;

  if (hasPipeline) {
    html += row('Confirmed BOB', confirmed, CONFIRMED_GREEN);
    html += row('Pipeline BOB', pipeline, TENTATIVE_AMBER);
  } else {
    html += row('Confirmed', confirmed, CONFIRMED_GREEN);
    html += row('Tentative', tentative, TENTATIVE_AMBER);
    const totalBob = confirmed + tentative;
    if (totalBob > 0) {
      const bobPct = budget > 0 ? Math.round((totalBob / budget) * 1000) / 10 : 0;
      html += `<div style="color:#94a3b8;font-size:11px;margin:2px 0 2px 14px">Total BOB: ${formatRupeesIndianCompact(totalBob)} (${bobPct}%)</div>`;
    }
  }

  if (actual > 0) {
    html += `<div style="border-top:1px solid rgba(148,163,184,0.15);margin:4px 0"></div>`;
    html += row('Actualized', actual, ACTUAL_PURPLE);
    if (budget > 0) {
      const achPct = Math.round((actual / budget) * 1000) / 10;
      html += `<div style="color:#94a3b8;font-size:11px;margin:2px 0 0 14px">Achievement: ${achPct}%</div>`;
    }
  }
  return html;
}

function buildMultiMonthOption(labels, budgetArr, confArr, pipeArr, actualArr) {
  const labelCount = labels.length;
  return {
    ...defaultAnimation,
    grid: standardGrid,
    tooltip: {
      ...premiumTooltip,
      formatter: tooltipFormatter,
    },
    legend: { show: false },
    xAxis: {
      ...indianCategoryAxis,
      data: labels,
      axisLabel: {
        ...indianCategoryAxis.axisLabel,
        rotate: labelCount > 12 ? 45 : 0,
        ...(labelCount > 16 ? { interval: 'auto' } : { interval: 0 }),
      },
    },
    yAxis: {
      ...indianValueAxis,
      axisLabel: {
        ...indianValueAxis.axisLabel,
        formatter: v => formatRupeesIndianCompact(v),
      },
    },
    series: [
      {
        name: 'Confirmed (Secured)',
        type: 'bar',
        stack: 'bob',
        data: confArr,
        barWidth: '28%',
        barGap: '30%',
        itemStyle: { color: CONFIRMED_GREEN, borderRadius: [0, 0, 0, 0] },
        emphasis: { itemStyle: { opacity: 0.85 } },
      },
      {
        name: 'Pipeline',
        type: 'bar',
        stack: 'bob',
        data: pipeArr,
        barWidth: '28%',
        itemStyle: { color: TENTATIVE_AMBER, borderRadius: [3, 3, 0, 0] },
        emphasis: { itemStyle: { opacity: 0.85 } },
      },
      {
        name: 'Actualized',
        type: 'bar',
        data: actualArr,
        barWidth: '28%',
        itemStyle: { color: ACTUAL_PURPLE, borderRadius: [3, 3, 0, 0] },
        emphasis: { itemStyle: { opacity: 0.85 } },
      },
      {
        name: 'Budget (Target)',
        type: 'line',
        data: budgetArr,
        symbol: 'circle',
        symbolSize: 7,
        z: 10,
        itemStyle: { color: BUDGET_LINE, borderColor: BUDGET_LINE, borderWidth: 2 },
        lineStyle: { type: 'solid', width: 2, color: BUDGET_LINE },
        connectNulls: true,
      },
    ],
  };
}

function buildDailyPacingOption(bobData) {
  const labels = bobData?.labels || [];
  const raw = bobData?.datasets || [];

  const paceDs = raw.find(d => { const L = norm(d.label); return L.includes('pace') || L.includes('budget'); });
  const confDs = raw.find(d => norm(d.label).includes('confirm'));
  const pipeDs = raw.find(d => norm(d.label).includes('pipeline'));
  const actDs = raw.find(d => norm(d.label).includes('actual'));

  const paceData = toNumArray(paceDs?.data);
  const confData = toNumArray(confDs?.data);
  const pipeData = toNumArray(pipeDs?.data);
  const actData = toNumArray(actDs?.data);
  const hasActualized = actData.some(v => v > 0);

  const makeLine = (name, data, color, style = 'solid', areaOpacity = 0) => ({
    name,
    type: 'line',
    data,
    smooth: 0.2,
    symbol: 'circle',
    symbolSize: 4,
    itemStyle: { color },
    lineStyle: { type: style, width: 3 },
    ...(areaOpacity > 0 ? { areaStyle: { opacity: areaOpacity } } : {}),
  });

  return {
    ...defaultAnimation,
    tooltip: { ...premiumTooltip, formatter: tooltipFormatter },
    legend: { show: false },
    grid: standardGrid,
    xAxis: {
      ...indianCategoryAxis,
      data: labels,
      axisLabel: {
        ...indianCategoryAxis.axisLabel,
        rotate: labels.length > 18 ? 45 : 0,
        interval: labels.length > 20 ? 'auto' : 0,
      },
    },
    yAxis: {
      ...indianValueAxis,
      axisLabel: {
        ...indianValueAxis.axisLabel,
        formatter: v => formatRupeesIndianCompact(v),
      },
    },
    series: [
      makeLine('Budget Pace', paceData, BUDGET_LINE, 'dashed'),
      makeLine('Confirmed BOB', confData, CONFIRMED_GREEN),
      makeLine('Pipeline BOB', pipeData, TENTATIVE_AMBER, 'solid', 0.08),
      ...(hasActualized ? [makeLine('Actualized Revenue', actData, ACTUAL_PURPLE)] : []),
    ],
  };
}

const BudgetOverviewChart = ({
  bobData,
  actualData,
  handleExport,
  showButton = true,
  reportDateRange,
}) => {
  const theme = useTheme();
  const isDailyPacing = Boolean(bobData?.dailyPacingChart);

  const { labels, budgetArr, confArr, pipeArr, actualArr } = useMemo(() => {
    const datasets = bobData?.datasets || [];
    const rawLabels = bobData?.labels || [];

    const budgetDs = findDs(datasets, 'budget') || datasets[0];
    const confirmedDs = findDs(datasets, 'confirm') || datasets[1];
    const pipelineDs = findDs(datasets, 'pipeline') || findDs(datasets, 'tentative') || datasets[2];
    const actualizedDs = findDs(datasets, 'actual');

    const bArr = toNumArray(budgetDs?.data);
    const cArr = toNumArray(confirmedDs?.data);
    const pArr = toNumArray(pipelineDs?.data);

    const aArr = actualizedDs?.data
      ? toNumArray(actualizedDs.data)
      : alignActualToLabels(rawLabels, actualData, reportDateRange);

    return { labels: rawLabels, budgetArr: bArr, confArr: cArr, pipeArr: pArr, actualArr: aArr };
  }, [bobData, actualData, reportDateRange]);

  const totalBudget = sumArr(budgetArr);
  const totalConf = sumArr(confArr);
  const totalPipeline = sumArr(pipeArr);
  const totalActual = sumArr(actualArr);

  const option = useMemo(() => {
    if (isDailyPacing) return buildDailyPacingOption(bobData);
    if (!labels.length) return {};
    return buildMultiMonthOption(labels, budgetArr, confArr, pipeArr, actualArr);
  }, [isDailyPacing, bobData, labels, budgetArr, confArr, pipeArr, actualArr]);

  const securedPct = totalBudget > 0 ? Math.round((totalConf / totalBudget) * 1000) / 10 : 0;
  const pipelinePct = totalBudget > 0 ? Math.round((totalPipeline / totalBudget) * 1000) / 10 : 0;
  const achievementPct = totalBudget > 0 ? Math.round((totalActual / totalBudget) * 1000) / 10 : 0;

  const chipColor = pct => {
    if (pct >= 90) return theme.palette.success.main;
    if (pct >= 60) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const badgeTrailing = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
      {bobData?.securedAchievementPercent && bobData.securedAchievementPercent !== '—' && (
        <Chip size="small" variant="outlined"
          label={`Secured: ${bobData.securedAchievementPercent}`}
          sx={miniChipSx(theme, theme.palette.success.main)}
        />
      )}
      {bobData?.pipelineCoveragePercent && bobData.pipelineCoveragePercent !== '—' && (
        <Chip size="small" variant="outlined"
          label={`Pipeline: ${bobData.pipelineCoveragePercent}`}
          sx={miniChipSx(theme, theme.palette.warning.main)}
        />
      )}
      {totalActual > 0 && (
        <Chip size="small" variant="outlined"
          label={`Actual: ${achievementPct}%`}
          sx={miniChipSx(theme, chipColor(achievementPct))}
        />
      )}
    </Box>
  );

  const displayName = bobData?.name || 'Budget Overview';
  const infoTooltip = bobData?.infoText || 'Budget vs confirmed, pipeline (enquiry + tentative + waitlisted) and actualized revenue';
  const apiKpis = kpiMapToItems(bobData?.kpiMap);

  return (
    <Paper sx={reportChartPaperSx}>
      <ReportCardHeader
        title={displayName}
        insight={infoTooltip}
        handleExport={handleExport}
        showButton={showButton}
        trailing={badgeTrailing}
      />

      <ReportKpiStrip
        items={apiKpis || [
          { label: 'Total Budget', value: formatRupeesIndianCompact(totalBudget) },
          { label: 'Confirmed', value: `${formatRupeesIndianCompact(totalConf)} (${securedPct}%)` },
          { label: 'Pipeline', value: `${formatRupeesIndianCompact(totalPipeline)} (${pipelinePct}%)` },
          { label: 'Actualized', value: `${formatRupeesIndianCompact(totalActual)} (${achievementPct}%)` },
        ]}
      />

      <ReportInsightBanner text={bobData?.insightText} style={bobData?.insightStyle} />

      <Box sx={reportBobChartCanvasSx}>
        <ReactECharts option={option} style={{ width: '100%', height: '100%' }} notMerge />
      </Box>
    </Paper>
  );
};

export default BudgetOverviewChart;
